import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Alert,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Divider,
  Paper,
} from "@mui/material";
import {
  People as PeopleIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { isAdmin, getCurrentUserId } from "../../services/AuthService";

interface AdminModule {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  permissions?: string[];
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [hasAdminAccess, setHasAdminAccess] = useState<boolean>(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAdminAccess = () => {
      try {
        const adminAccess = isAdmin();
        const userId = getCurrentUserId();
        setHasAdminAccess(adminAccess);
        setCurrentUserId(userId);

        if (!adminAccess) {
          navigate("/");
        }
      } catch (err) {
        setError("Admin check failed");
        setHasAdminAccess(false);
      }
    };

    checkAdminAccess();
  }, [navigate]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<number>(() => {
    const tabFromParams = searchParams.get("tab");
    return tabFromParams === "1" ? 1 : 0;
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Update URL search params to preserve tab state
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", newValue.toString());
    setSearchParams(newSearchParams);
  };

  const userManagementModules: AdminModule[] = [
    {
      title: "User Management",
      description: "Manage user accounts, roles, and permissions",
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      path: "/admin/users",
      permissions: ["administration:admin"],
    },
    {
      title: "Role & Context Management",
      description: "Manage user roles and contexts",
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      path: "/admin/roles",
      permissions: ["administration:admin"],
    },
  ];

  const handleModuleClick = (path: string) => {
    try {
      navigate(path);
    } catch (error) {
      console.error("Navigation error:", error);
    }
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Access denied. Unable to verify administrator privileges.
        </Alert>
      </Container>
    );
  }

  if (!hasAdminAccess) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Access denied. You need administrator privileges to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ mt: 4, mb: 4 }}
      data-testid="admin-dashboard-container"
    >
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar
          sx={{
            width: 56,
            height: 56,
            border: "1px solid",
            borderColor: "divider",
          }}
          data-testid="admin-avatar"
          alt="Admin user avatar"
        >
          <AdminIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Admin Dashboard
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label="Administrator"
              variant="outlined"
              size="small"
              icon={<SecurityIcon />}
              data-testid="security-badge"
            />
            <Typography variant="body2" color="text.secondary">
              User ID: {currentUserId}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Welcome Message */}
      <Alert severity="info" sx={{ mb: 4 }}>
        Welcome to the admin panel. Manage users, roles, permissions, and system
        data models. Admin permissions required. Please use these tools
        responsibly.
      </Alert>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="admin sections"
        >
          <Tab label="User & Role Management" />
        </Tabs>
      </Box>

      {/* User & Role Management Section */}
      {activeTab === 0 && (
        <Box>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
            User & Role Management
          </Typography>

          <Grid container spacing={3} data-testid="user-management-grid">
            {userManagementModules.map((module, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      borderColor: "text.primary",
                    },
                  }}
                  data-testid="admin-module-paper"
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mb: 2,
                    }}
                    data-testid="module-icon"
                  >
                    {module.icon}
                  </Box>

                  <Typography
                    variant="h6"
                    component="h2"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    {module.title}
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minHeight: "40px", textAlign: "center", mb: 2 }}
                  >
                    {module.description}
                  </Typography>

                  {module.permissions && (
                    <Box sx={{ mt: "auto", textAlign: "center" }}>
                      {module.permissions.map((permission, permIndex) => (
                        <Chip
                          key={permIndex}
                          label={permission}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.75rem", height: "20px", mb: 1 }}
                          data-testid="permission-chip"
                        />
                      ))}
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ textAlign: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => handleModuleClick(module.path)}
                    >
                      Open Module
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Model Management Section */}
      {activeTab === 1 && (
        <Box>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
            Model Management
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Manage system data models. Each model supports full CRUD operations
            (Create, Read, Update, Delete).
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default AdminDashboard;
